<script lang="ts">
  /* A TIDE STATION, opened.
   *
   * ═══ WHAT A TIDE POPUP IS ACTUALLY FOR ═══
   *
   * "How much water, when, and which way is it going." The chart symbol answers
   * the last two at a glance and deliberately cannot answer the first — an
   * eight-step bar is a picture of a fraction, not a height. So this puts
   * numbers on it: the height now, this cycle's low and high, the rate, the
   * next turn, a curve for the next 24 hours with its own axis labelled, and a
   * finder for "when is it next above X feet", which is the anchoring question.
   *
   * ═══ THE DATUM, SAID OUT LOUD, TWICE ═══
   *
   * Every height here is on THE STATION'S OWN DATUM. The chart symbol never
   * needs to say so because it only ever uses differences and the datum cancels
   * — that is the reasoning `lib/tidestations.ts` is built on. A curve cannot
   * do that: it has to put numbers on an axis, and an unlabelled height would
   * invite somebody to subtract it from a sounding. So it says so under the
   * curve, and a secondary station says that it is one.
   *
   * ═══ AND IT NEVER EXTRAPOLATES ═══
   *
   * Past the shipped year there is no curve, and the sheet says why. Not a
   * flattened line, not a guess — people take ground on these numbers.
   */
  import {
    FT,
    nextTimeHeightAtLeast,
    tideAt,
    type TideStation
  } from '@gennetay/chart-core/lib/tidestations';
  import { untilText } from '@gennetay/chart-core/lib/currents';
  import { viewTime } from '@gennetay/chart-core/lib/viewtime.svelte';

  interface Props {
    station: TideStation;
    /**
     * Whether to print the station's name and code.
     *
     * The boat's sheet already has a header — the range, the bearing and the
     * position from the vessel, which this deployment cannot compute — so it
     * turns this off rather than saying the name twice, which is what it did on
     * the first render of the shared component.
     */
    header?: boolean;
    /** Ticked once a second by the host so the readouts follow the clock while
     *  the cursor is live. See `Scrubber` — same reasoning. */
    tick?: number;
  }
  const { station, header = true, tick = 0 }: Props = $props();

  /* Read through the tick so a live sheet updates; `viewTime.now` is the shared
   * cursor, so parking the chart's scrubber moves this sheet with it. */
  const at = $derived((void tick, viewTime.now));
  const now = $derived(tideAt(station, at));

  const W = 300;
  const H = 84;

  /* 24 hours at 20-minute steps. Null rather than a partial curve past the end
   * of the data — see the header. */
  const curve = $derived.by(() => {
    const pts: { t: number; m: number }[] = [];
    for (let i = 0; i <= 72; i++) {
      const t = at + i * 20 * 60_000;
      const a = tideAt(station, t);
      if (!a) return null;
      pts.push({ t, m: a.m });
    }
    const lo = Math.min(...pts.map((p) => p.m));
    const hi = Math.max(...pts.map((p) => p.m));
    const span = Math.max(0.2, hi - lo);
    const end = at + 24 * 3_600_000;
    return { pts, lo, hi, span, events: station.events.filter((e) => e.t >= at && e.t <= end) };
  });

  const path = $derived.by(() => {
    const c = curve;
    if (!c) return '';
    return c.pts
      .map((p, i) => {
        const x = (i / (c.pts.length - 1)) * W;
        const y = H - 4 - ((p.m - c.lo) / c.span) * (H - 8);
        return `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ');
  });

  /* Feet, because every tide reading on this chart is feet — and metres beside
   * it, because that is what the station's own tables publish and somebody
   * checking against them needs it. */
  const ft = (m: number) => `${(m * FT).toFixed(1)} ft`;
  const hhmm = (t: number) =>
    new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  const dayName = (t: number) =>
    new Date(t).toLocaleDateString([], { weekday: 'short' }).toUpperCase();
  /* A 24-hour list crosses midnight, so "21:49" followed by "05:18" reads as a
   * jump backwards unless the day is marked. Marked, not dated: the only
   * distinction that exists inside 24 hours is today or tomorrow. */
  const dayMark = (t: number) => (new Date(t).getDate() === new Date(at).getDate() ? '' : ' +1');

  /* ── NEXT ≥ X FT ──
   *
   * The anchoring question, and the reason the pure finder in chart-core walks
   * the event segments rather than sampling: it solves the crossing exactly on
   * the same half-cosine the curve draws, so the answer cannot land between two
   * samples and disagree with the picture above it. */
  let findFt = $state<number | null>(10);
  const findValid = $derived(findFt != null && Number.isFinite(findFt) && findFt > 0);
  const found = $derived.by(() => {
    const v = findFt;
    if (v == null || !Number.isFinite(v) || v <= 0) return null;
    return nextTimeHeightAtLeast(station, at, v);
  });
</script>

<div class="tide">
  {#if header}
    <header>
      <strong>{station.name}</strong>
      <span class="sub">{station.code}</span>
    </header>
  {/if}

  <section>
    <div class="lbl">NOW{viewTime.isLive ? '' : ` · ${dayName(at)} ${hhmm(at)}`}</div>
    <dl>
      <dt>HEIGHT</dt>
      <dd>{now ? `${ft(now.m)} · ${now.m.toFixed(2)} m` : '—'}</dd>
      <dt>THIS CYCLE</dt>
      <dd>{now ? `${ft(now.low)} to ${ft(now.high)}` : '—'}</dd>
      <dt>STATE</dt>
      <dd>{now ? (now.rising ? '▲ making' : '▼ taking off') : '—'}</dd>
      <dt>RATE</dt>
      <dd>{now ? `${(Math.abs(now.ratePerHour) * FT).toFixed(1)} ft/h` : '—'}</dd>
      <dt>NEXT {now?.rising ? 'HIGH' : 'LOW'}</dt>
      <dd>{now ? `${hhmm(now.next.t)} · ${untilText(now.next.t - at)}` : '—'}</dd>
    </dl>
    {#if station.derived}
      <!-- Never equal authority with a reference station. -->
      <p class="warn">
        Secondary station — synthesised from table corrections rather than published directly.
      </p>
    {/if}
  </section>

  <section>
    <div class="lbl">NEXT 24 HOURS</div>
    {#if curve}
      <!-- A CURVE WITHOUT NUMBERS IS A PICTURE. The shape says when; only the
           axis says how much, and "how much water" is the question a tide
           station is opened to answer. -->
      <svg viewBox="0 0 {W} {H}" role="img" aria-label="tide, next 24 hours">
        {#each [6, 12, 18] as h (h)}
          <line x1={(h / 24) * W} y1="0" x2={(h / 24) * W} y2={H} class="grid" />
        {/each}
        <line x1="0" y1="4" x2={W} y2="4" class="grid" />
        <line x1="0" y1={H - 4} x2={W} y2={H - 4} class="grid" />
        <path d={path} class="line" />
        <text x="2" y="12">{(curve.hi * FT).toFixed(1)} ft</text>
        <text x="2" y={H - 6}>{(curve.lo * FT).toFixed(1)} ft</text>
        <!-- Where the tide is at this moment, so the curve is anchored to now
             rather than being an abstract shape starting at the edge. -->
        {#if now}
          <circle cx="3" cy={H - 4 - ((now.m - curve.lo) / curve.span) * (H - 8)} r="3" class="dot" />
        {/if}
      </svg>
      <div class="axis sub">
        <span>now</span><span>+6h</span><span>+12h</span><span>+18h</span><span>+24h</span>
      </div>
      <dl>
        {#each curve.events.slice(0, 6) as e (e.t)}
          <dt>{e.m >= (curve.lo + curve.hi) / 2 ? 'HIGH' : 'LOW'}</dt>
          <dd>{hhmm(e.t)}{dayMark(e.t)} · {ft(e.m)} · {e.m.toFixed(2)} m</dd>
        {/each}
      </dl>
      <p class="sub">
        Heights are on THIS STATION'S datum, which is what they are published against — not a
        depth under the boat.
      </p>
    {:else}
      <p class="sub">No prediction — the shipped dataset does not reach that far.</p>
    {/if}
  </section>

  <section>
    <div class="lbl">NEXT ≥ X FT</div>
    <div class="find">
      <input type="number" min="0" step="0.5" bind:value={findFt} aria-label="height in feet" />
      <span class="sub">ft</span>
    </div>
    {#if !findValid}
      <p class="sub">Enter a height in feet — finds the next tide at or above it.</p>
    {:else if found}
      <div class="found">{dayName(found.t)} {hhmm(found.t)} · {ft(found.m)}</div>
    {:else}
      <p class="sub">No tide at or above {findFt} ft in the next 48 h.</p>
    {/if}
  </section>
</div>

<style>
  /* SELF-CONTAINED, like everything in this package. The dash's copy of this
     leaned on its global utility classes, which is exactly what stopped it
     being shareable — a token where the host has one, a sane value where it
     has not. */
  .tide {
    display: flex;
    flex-direction: column;
    gap: 14px;
    color: var(--ink);
    font: 12px/1.45 var(--mono);
  }
  header {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
  header strong {
    font-size: 13px;
  }
  /* `.lbl` and `dt` are the same thing — a small caps-ish label — so they are
     one rule. They were two, which cost the dash 71 B of its CSS budget for no
     difference on screen. */
  /* Silkscreen for labels, Chivo for numbers — the boat's own pairing, which
     the shared components could not ask for until the families became tokens. */
  .lbl,
  dt {
    font-family: var(--px);
    font-size: 10px;
    letter-spacing: 1px;
    color: var(--sub);
  }
  .lbl {
    margin-bottom: 5px;
  }
  .sub {
    color: var(--sub);
    font-size: 11px;
  }
  p {
    margin: 6px 0 0;
  }
  .warn {
    color: var(--am);
  }
  /* Two columns, label then value, so the numbers line up down the right and
     can be compared without reading the labels again. */
  dl {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2px 12px;
    margin: 0;
  }
  dt {
    align-self: baseline;
  }
  dd {
    margin: 0;
  }
  svg {
    width: 100%;
    height: auto;
    display: block;
    margin: 4px 0 2px;
  }
  .grid {
    stroke: var(--grid);
    stroke-width: 1;
  }
  .line {
    fill: none;
    stroke: var(--cy);
    stroke-width: 1.6;
  }
  .dot {
    fill: var(--cy);
  }
  text {
    fill: var(--sub);
    font: 9px var(--mono);
  }
  .axis {
    display: flex;
    justify-content: space-between;
  }
  .find {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  input {
    width: 6em;
    background: transparent;
    border: 1px solid var(--line);
    border-radius: 5px;
    color: inherit;
    font: inherit;
    /* A finger, not a mouse. */
    min-height: 30px;
    padding: 4px 7px;
  }
  .found {
    font-size: 13px;
    margin-top: 6px;
  }

  /* ═══ A PHONE, WHICH IS NOT A WIDTH ═══
   *
   * Held sideways one is 734-852 px wide and under 400 tall, so the query asks
   * about both. The boat's iPad is 1024 x 768 and matches neither arm, so it
   * keeps the denser control its own layout and its own proofs are built
   * around. */
  @media (pointer: coarse) and (max-width: 640px),
    (pointer: coarse) and (max-height: 500px) {
    /* The `NEXT >= X FT` field. 30 px, and it takes focus — so on iOS it also
       needs 16 px of text or the whole viewport zooms when you tap it. */
    input[type='number'] {
      min-height: 44px;
      font-size: 16px;
    }
  }
</style>
